import type {DetailRow, E123Config, GroupConfig} from './types';

// "What's covered" rows already entered on any site, offered in the builder's "Copy from…"
// list. Ranked so the likeliest source is first: same e123 product (same PDID = same
// coverage), then same plan name ("Short Term" and "Short-Term" match), then the rest.

export interface DetailRowDefault {
  sourceSlug: string;
  planId: string;
  planName: string;
  pdid: number | null;
  nameKey: string;
  rows: DetailRow[];
}

export interface RankedDetailRowDefault extends DetailRowDefault {
  match: 'product' | 'name' | null;
}

export const planNameKey = (name: string) => name.toLowerCase().replace(/[^a-z0-9]/g, '');

/** Sites in priority order (first wins when two carry identical rows). */
export function collectDetailRowDefaults(
  sites: {groupSlug: string; config: GroupConfig; e123: E123Config}[],
): DetailRowDefault[] {
  const out: DetailRowDefault[] = [];
  const seen = new Set<string>();
  for (const site of sites) {
    for (const plan of site.config.plans) {
      if (!plan.detailRows?.length) continue;
      const pdid = site.e123.planPdid[plan.id] ?? null;
      // Copied sites repeat the same rows; list each distinct set once.
      const key = `${pdid}|${planNameKey(plan.name)}|${JSON.stringify(plan.detailRows)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        sourceSlug: site.groupSlug,
        planId: plan.id,
        planName: plan.name,
        pdid,
        nameKey: planNameKey(plan.name),
        rows: plan.detailRows,
      });
    }
  }
  return out;
}

export function rankDetailRowDefaults(
  defaults: DetailRowDefault[],
  plan: {id: string; name: string; pdid?: number},
  groupSlug: string,
): RankedDetailRowDefault[] {
  const key = planNameKey(plan.name);
  const ranked = defaults
    .filter((d) => !(d.sourceSlug === groupSlug && d.planId === plan.id))
    .map(
      (d): RankedDetailRowDefault => ({
        ...d,
        match:
          plan.pdid !== undefined && d.pdid === plan.pdid
            ? 'product'
            : key && d.nameKey === key
              ? 'name'
              : null,
      }),
    );
  const order = {product: 0, name: 1} as const;
  return ranked.sort((a, b) => (a.match ? order[a.match] : 2) - (b.match ? order[b.match] : 2));
}
